import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Accordion, AccordionDetails, Grid } from '@material-ui/core';
import { AmountField, TextField, AccordionSummary, HilightChanges, H3 } from '~/components';
import { useEditModeAccordionClass } from '~/hooks/useEditModeContext';
import { l } from '~/resources/pricings/PricingShow';
import { RegularFeeType } from '../ConfirmationDrawers/FeeConfirmationField';
import { compareRules } from '../Forms/CounterBased';
import { makeStyles } from '@material-ui/core/styles';
import { Rule } from '~/types/pricings';
import FormatLineSpacingIcon from '@material-ui/icons/FormatLineSpacing';

interface CounterBasedViewProps {
  curr: RegularFeeType;
  prev: RegularFeeType;
}

export const CounterBasedView = (props: CounterBasedViewProps) => {
  const { curr, prev } = props;
  const classes = useStyles();
  const editModeAccordionClass = useEditModeAccordionClass();
  const translate = useTranslate();
  const t = (key: string, args: any = 1): string => translate(l(key), args);
  const [expanded, setExpanded] = useState<{ [x: string]: boolean }>({});
  const handleChange = (key: string, isExpanded: boolean) => () => {
    setExpanded((prevProps) => ({ ...prevProps, [key]: !isExpanded }));
  };
  return (
    <>
      <Grid item xs={12}>
        <TextField label={t('currency')}>
          <HilightChanges curr={curr?.currency} prev={prev?.currency}>
            {curr?.currency}
          </HilightChanges>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField label={t('dynamicTypeCounterReference')}>
          <HilightChanges
            curr={curr?.dynamicTypeCounterReference}
            prev={prev?.dynamicTypeCounterReference}
          >
            {curr?.dynamicTypeCounterReference}
          </HilightChanges>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <H3 icon={<FormatLineSpacingIcon />} noPaddingLeft bottomSpacing={0} topSpacing={0}>
          {t('rules')}
        </H3>
      </Grid>
      {curr?.defaultRule && (
        <Grid item xs={12}>
          <Accordion
            expanded={expanded[`panel-default`]}
            onChange={handleChange(`panel-default`, expanded[`panel-default`])}
            className={editModeAccordionClass}
          >
            <AccordionSummary
              aria-controls={`default-content`}
              id={`default-header`}
              expanded={expanded[`panel-default`]}
              heading={
                <HilightChanges curr={curr?.defaultRule} prev={prev?.defaultRule}>
                  <div className={classes.ruleHeader}>{t('defaultRule')}</div>
                </HilightChanges>
              }
            />
            <AccordionDetails key='external-sources-details' className={classes.AccordionDetails}>
              <Grid container direction='column' spacing={2}>
                <Grid item xs={12}>
                  <TextField label={t('feeAlgorithm')}>
                    <HilightChanges curr={curr?.defaultRule?.type} prev={prev?.defaultRule?.type}>
                      {t(curr?.defaultRule?.type, 2)}
                    </HilightChanges>
                  </TextField>
                </Grid>
                {curr?.defaultRule?.type === 'fixed' && (
                  <Grid item xs={12}>
                    <TextField label={t('amount')}>
                      <HilightChanges
                        curr={curr?.defaultRule?.amount}
                        prev={prev?.defaultRule?.amount}
                      >
                        <AmountField amount={curr?.defaultRule?.amount} />
                      </HilightChanges>
                    </TextField>
                  </Grid>
                )}
                {curr?.defaultRule?.type === 'percent' && (
                  <>
                    <Grid item xs={12}>
                      <TextField label={t('percent', 2)}>
                        <HilightChanges
                          curr={curr?.defaultRule?.percent}
                          prev={prev?.defaultRule?.percent}
                        >
                          {curr?.defaultRule?.percent?.toString() + '%'}
                        </HilightChanges>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container direction='row' spacing={2}>
                        <Grid item xs={6}>
                          <TextField label={t('minAmount')}>
                            <HilightChanges
                              curr={curr?.defaultRule?.minAmount}
                              prev={prev?.defaultRule?.minAmount}
                            >
                              <AmountField amount={curr?.defaultRule?.minAmount} />
                            </HilightChanges>
                          </TextField>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField label={t('maxAmount')}>
                            <HilightChanges
                              curr={curr?.defaultRule?.maxAmount}
                              prev={prev?.defaultRule?.maxAmount}
                            >
                              <AmountField amount={curr?.defaultRule?.maxAmount} />
                            </HilightChanges>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                {curr?.defaultRule?.type === 'fixedPlusPercent' && (
                  <>
                    <Grid item xs={12}>
                      <TextField label={t('amount')}>
                        <HilightChanges
                          curr={curr?.defaultRule?.amount}
                          prev={prev?.defaultRule?.amount}
                        >
                          <AmountField amount={curr?.defaultRule?.amount} />
                        </HilightChanges>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label={t('percent', 2)}>
                        <HilightChanges
                          curr={curr?.defaultRule?.percent}
                          prev={prev?.defaultRule?.percent}
                        >
                          {curr?.defaultRule?.percent?.toString() + '%'}
                        </HilightChanges>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container direction='row' spacing={2}>
                        <Grid item xs={6}>
                          <TextField label={t('minAmountOfPercentPart')}>
                            <HilightChanges
                              curr={curr?.defaultRule?.minAmount}
                              prev={prev?.defaultRule?.minAmount}
                            >
                              <AmountField amount={curr?.defaultRule?.minAmount} />
                            </HilightChanges>
                          </TextField>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField label={t('maxAmountOfPercentPart')}>
                            <HilightChanges
                              curr={curr?.defaultRule?.maxAmount}
                              prev={prev?.defaultRule?.maxAmount}
                            >
                              <AmountField amount={curr?.defaultRule?.maxAmount} />
                            </HilightChanges>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextField label={t('shouldIncreaseCounter')}>
                    <HilightChanges
                      curr={curr?.defaultRule?.shouldIncreaseCounter}
                      prev={prev?.defaultRule?.shouldIncreaseCounter}
                    >
                      {curr?.defaultRule?.shouldIncreaseCounter ? t('yes') : t('no')}
                    </HilightChanges>
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
      {curr?.rules
        ?.sort((firstRule: Rule, secondRule: Rule) => {
          return compareRules(firstRule, secondRule);
        })
        .map((item: Rule, index: number) => {
          const isExpanded = Boolean(expanded[`panel-${index}`]);
          return (
            <Grid key={index} item xs={12}>
              <Accordion
                expanded={isExpanded}
                onChange={handleChange(`panel-${index}`, isExpanded)}
                key={index}
                className={editModeAccordionClass}
              >
                <AccordionSummary
                  aria-controls={`${index}-content`}
                  id={`${index}-header`}
                  expanded={isExpanded}
                  heading={
                    <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                      <TextField label={t('rule', { num: index + 1 })}>
                        {t('minCounterMaxCounter', {
                          min: item?.minCounterValue,
                          max: item?.maxCounterValue,
                        })}
                      </TextField>
                    </HilightChanges>
                  }
                />
                <AccordionDetails
                  key='external-sources-details'
                  className={classes.AccordionDetails}
                >
                  <Grid container direction='column' spacing={2}>
                    <Grid container item direction='row' spacing={2}>
                      <Grid item xs={6}>
                        <TextField label={t('minTransferAmount')}>
                          <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                            <AmountField amount={item?.minTransferAmount} />
                          </HilightChanges>
                        </TextField>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField label={t('maxTransferAmount')}>
                          <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                            <AmountField amount={item?.maxTransferAmount} />
                          </HilightChanges>
                        </TextField>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label={t('feeAlgorithm')}>
                        <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                          {t(item.type, 2)}
                        </HilightChanges>
                      </TextField>
                    </Grid>
                    {item.type === 'fixed' && (
                      <Grid item xs={12}>
                        <TextField label={t('amount')}>
                          <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                            <AmountField amount={item?.amount} />
                          </HilightChanges>
                        </TextField>
                      </Grid>
                    )}
                    {item.type === 'percent' && (
                      <>
                        <Grid item xs={12}>
                          <TextField label={t('percent', 2)}>
                            <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                              {item?.percent?.toString() + '%'}
                            </HilightChanges>
                          </TextField>
                        </Grid>
                        <Grid item>
                          <Grid container direction='row' spacing={2}>
                            <Grid item xs={6}>
                              <TextField label={t('minAmount')}>
                                <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                                  <AmountField amount={item?.minAmount} />
                                </HilightChanges>
                              </TextField>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField label={t('maxAmount')}>
                                <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                                  <AmountField amount={item?.maxAmount} />
                                </HilightChanges>
                              </TextField>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}
                    {item.type === 'fixedPlusPercent' && (
                      <>
                        <Grid item xs={12}>
                          <TextField label={t('amount')}>
                            <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                              <AmountField amount={item?.amount} />
                            </HilightChanges>
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField label={t('percent', 2)}>
                            <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                              {item?.percent?.toString() + '%'}
                            </HilightChanges>
                          </TextField>
                        </Grid>
                        <Grid item>
                          <Grid container direction='row' spacing={2}>
                            <Grid item xs={6}>
                              <TextField label={t('minAmountOfPercentPart')}>
                                <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                                  <AmountField amount={item?.minAmount} />
                                </HilightChanges>
                              </TextField>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField label={t('maxAmountOfPercentPart')}>
                                <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                                  <AmountField amount={item?.maxAmount} />
                                </HilightChanges>
                              </TextField>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <TextField label={t('shouldIncreaseCounter')}>
                        <HilightChanges curr={curr?.rules} prev={prev?.rules}>
                          {item?.shouldIncreaseCounter ? t('yes') : t('no')}
                        </HilightChanges>
                      </TextField>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  AccordionDetails: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(3),
    display: 'block',
  },
  ruleHeader: {
    fontSize: 10,
    fontWeight: 'normal',
    marginBottom: 4,
  },
}));

export default CounterBasedView;
